import { Injectable } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, concatMap, map, retry, switchMap, take, tap } from 'rxjs/operators';
import { Logger, LoggingService } from 'ionic-logging-service';
import { TenantService } from '@app/services/tenant/tenant.service';
import { StorageService } from '@app/services/storage/storage.service';
import { PlatformService } from '@app/services/platform/platform.service';
import { ITenantLite } from '@app/services/tenant/models/tenant-lite.model';
import { ITenant } from '@app/services/tenant/models/tenant.model';

export type PostAuthDestination = 'app' | 'tenant-select';

/**
 * LoginService handles the post-authentication tenant resolution.
 *
 * After a user successfully authenticates (on both native and web platforms),
 * this service determines whether the user needs to see the tenant-select page
 * or can be navigated directly to the app (Print page).
 *
 * The tenant list is ALWAYS fetched from the server because it contains the
 * necessary HATEOAS links for resources the app needs.
 *
 * Resolution logic:
 * 1. If tenantDomain is set (from browser URL or alternate sign-in input)
 *    AND a matching tenant is found in the list → auto-select, return 'app'
 * 2. If lastUsedTenant exists in storage AND is found in the list → auto-select, return 'app'
 * 3. If only 1 tenant in the list → auto-select, return 'app'
 * 4. Otherwise → return 'tenant-select' for the component to navigate
 */
@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private logger: Logger;
  private isCordova: boolean;

  constructor(
    private loggingService: LoggingService,
    private platformService: PlatformService,
    private storageService: StorageService,
    private tenantService: TenantService
  ) {
    this.logger = this.loggingService.getLogger('[LoginService]');
    const methodName = 'ctor';
    this.logger.entry(methodName);
    this.isCordova = this.platformService.isCordova();
  }

  // ############################################### //
  // ### POST-AUTH TENANT RESOLUTION  ############## //
  // ############################################### //

  /**
   * Main entry point: called after successful authentication.
   * Fetches the tenant list and decides whether the user should go to the app
   * or to the tenant-select page. Returns an Observable with the destination.
   *
   * Navigation is handled by the calling component, not by this service.
   */
  public resolvePostAuthNavigation$(): Observable<PostAuthDestination> {
    const methodName = 'resolvePostAuthNavigation$() ';
    this.logger.info(methodName);

    const tenantDomain = this.tenantService.tenantDomain;
    const environmentDomain = this.tenantService.environmentDomain;
    const hasPreSelectedTenant = tenantDomain && !environmentDomain;

    return this.fetchTenantList$().pipe(
      switchMap((tenantList: ITenantLite[]) => {
        if (!tenantList || tenantList.length === 0) {
          this.logger.info(methodName + 'Empty tenant list, destination: tenant-select');
          return of('tenant-select' as const);
        }

        // Step 1: If tenantDomain is set by user input (browser URL or alternate sign-in)
        if (hasPreSelectedTenant) {
          const matchingTenant = this.findTenantByHostingDomain(tenantDomain, tenantList);
          if (matchingTenant) {
            this.logger.info(methodName + 'Tenant domain matched in list, auto-selecting: ' + tenantDomain);
            return this.activateTenant$(matchingTenant).pipe(
              map(() => 'app' as const)
            );
          }
          // Tenant domain was set but not found in list — fall through to other checks
          this.logger.info(methodName + 'Tenant domain "' + tenantDomain + '" not found in tenant list, checking other options');
        }

        // Step 2: Check lastUsedTenant from storage
        return this.getLastUsedTenant$().pipe(
          switchMap((lastUsedTenant: ITenantLite | null) => {
            if (lastUsedTenant) {
              const matchingTenant = tenantList.find(t => t.links.self === lastUsedTenant.links?.self);
              if (matchingTenant) {
                this.logger.info(methodName + 'lastUsedTenant found in tenant list, auto-selecting');
                return this.activateTenant$(matchingTenant).pipe(
                  map(() => 'app' as const)
                );
              }
              this.logger.info(methodName + 'lastUsedTenant not found in current tenant list');
            }

            // Step 3: If only 1 tenant in list, auto-select
            if (tenantList.length === 1) {
              this.logger.info(methodName + 'Only 1 tenant in list, auto-selecting');
              return this.activateTenant$(tenantList[0]).pipe(
                map(() => 'app' as const)
              );
            }

            // Step 4: Multiple tenants, user must choose
            this.logger.info(methodName + 'Multiple tenants (' + tenantList.length + '), destination: tenant-select');
            return of('tenant-select' as const);
          })
        );
      })
    );
  }

  // ############################################### //
  // ### FETCH TENANT LIST  ######################## //
  // ############################################### //

  public fetchTenantList$(): Observable<ITenantLite[]> {
    this.logger.info('fetchTenantList$()');

    return this.tenantService.getTenantListForUser().pipe(
      // On native, after returning from SafariViewController/InAppBrowser, the WebView's
      // network stack may not be fully re-established, causing transient "status: 0 Unknown Error"
      // failures. Retry up to 2 times with a 2-second delay for these specific errors only.
      retry({
        count: 2,
        delay: (error, retryCount) => {
          if (this.isCordova && error?.status === 0) {
            this.logger.info('fetchTenantList$() - Transient network error (status: 0) on native, retry ' + retryCount + ' of 2');
            return timer(2000);
          }
          // Don't retry non-transient errors (4xx, 5xx, etc.)
          return throwError(() => error);
        }
      }),
      take(1),
      tap((tenantList: ITenantLite[]) => {
        this.logger.info('fetchTenantList$() - got TenantList with length: ' + tenantList.length);
      })
    );
  }

  // ############################################### //
  // ### LAST USED TENANT  ######################### //
  // ############################################### //

  private getLastUsedTenant$(): Observable<ITenantLite | null> {
    this.logger.info('getLastUsedTenant$()');
    return this.storageService.getItemFromLocalStorage('lastUsedTenant', 'LoginService.getLastUsedTenant$()').pipe(
      take(1),
      switchMap((lastUsedTenant: ITenantLite) => {
        return lastUsedTenant ? of(lastUsedTenant) : of(null);
      }),
      catchError(e => {
        this.logger.info('getLastUsedTenant$() - error getting lastUsedTenant: ' + e);
        return of(null);
      })
    );
  }

  // ############################################### //
  // ### ACTIVATE TENANT (SELECT + SETUP)  ######### //
  // ############################################### //

  /**
   * Activates a tenant: grants access if needed, fetches full tenant resource,
   * stores the tenant with the correct currentUser link, and clears idpLoginToken.
   *
   * The full tenant resource (ITenant) is fetched to obtain the correct px:currentuser
   * link. The ITenantLite from the tenant list contains a px:tenant-user link which
   * returns a lighter user object without authentication_details. App pages refresh
   * both tenant and user on their own, so this method does not fetch the user resource.
   *
   * Used by both resolvePostAuthNavigation$() (auto-select path)
   * and TenantSelectPage (manual selection, handles its own navigation and error recovery).
   *
   * @param tenant - The tenant to activate
   * @returns Observable that completes after all activation steps succeed
   */
  public activateTenant$(tenant: ITenantLite): Observable<void> {
    this.logger.info('activateTenant$() - tenant: ' + tenant.tenantData.hostingDomain);

    // Step 1: If the tenant has a grantTenantAccess link, the user needs to be granted access first.
    const grantAccess$: Observable<any> = tenant.links.grantTenantAccess
      ? this.grantTenantAccessIfNeeded$(tenant)
      : of(null);

    return grantAccess$.pipe(
      // Step 2: Get the full tenant resource (with embedded PUSHER_CLIENT_INFO, securePrintConfiguration).
      // This is required to obtain the correct px:currentuser link for storage.
      concatMap(() => this.tenantService.getTenantResource(tenant.links.self).pipe(take(1))),
      // Step 3: Store the ITenantLite with the full tenant's currentUser link.
      // The ITenantLite from the tenant list uses the px:tenant-user link for currentUser,
      // which returns a lighter user object without authentication_details. The full tenant
      // resource (ITenant) has the correct px:currentuser link. We must persist the full
      // tenant's currentUser link so that refreshCurrentUser() (which reads lastUsedTenant
      // from storage) uses the correct endpoint.
      concatMap((fullTenant: ITenant) => {
        const tenantToStore: ITenantLite = {
          ...tenant,
          links: {
            ...tenant.links,
            currentUser: fullTenant.links.currentUser
          }
        };
        return this.storageService.addItemToLocalStorage('lastUsedTenant', tenantToStore, 'LoginService.activateTenant$()').pipe(take(1));
      }),
      // Step 4: Clear the idpLoginToken — it is a one-time token and should not be re-sent
      concatMap(() => this.clearIdpLoginToken$()),
      // Step 5: Clear the tenant list signal — the list has served its purpose.
      // This ensures "Change Home" always fetches a fresh list from the server
      // instead of reusing the stale post-auth list (which may include IDP-grantable tenants).
      tap(() => this.tenantService.tenantListSignal.set([])),
      map(() => void 0)
    );
  }

  /**
   * Clears the one-time idpLoginToken from storage. Safe to call when we know
   * the post-auth flow is no longer in progress (e.g. successful activation,
   * or returning-user cold start where verifyUserCredentials has succeeded).
   *
   * A stale idpLoginToken forces external-api to call printix-internal-auth on
   * every /v1/users/me request, exposing the user to transient service-to-service
   * failures even when their own session is healthy.
   */
  public clearIdpLoginToken$(): Observable<void> {
    return this.storageService.addItemToLocalStorage('idpLoginToken', null, 'LoginService.clearIdpLoginToken$()').pipe(take(1));
  }

  /**
   * Grants the user access to a tenant by reading the idpLoginToken from storage
   * and calling the grantTenantAccess endpoint.
   */
  private grantTenantAccessIfNeeded$(tenant: ITenantLite): Observable<any> {
    this.logger.info('grantTenantAccessIfNeeded$() - Tenant requires access grant');
    const grantAccessUrl = tenant.links.grantTenantAccess;
    if (!grantAccessUrl) {
      return of(null);
    }
    return this.storageService.getItemFromLocalStorage('idpLoginToken', 'LoginService.grantTenantAccessIfNeeded$()').pipe(
      take(1),
      switchMap((idpLoginToken: string | null) => {
        if (!idpLoginToken) {
          this.logger.info('grantTenantAccessIfNeeded$() - No idpLoginToken available, skipping grant');
          return of(null);
        }
        return this.tenantService.grantTenantAccess(grantAccessUrl, idpLoginToken);
      })
    );
  }

  // ############################################### //
  // ### FIND TENANT BY HOSTING DOMAIN  ############ //
  // ############################################### //

  private findTenantByHostingDomain(tenantHostingDomain: string, tenantList: ITenantLite[]): ITenantLite | null {
    this.logger.info('findTenantByHostingDomain() - looking for: ' + tenantHostingDomain);
    const found = tenantList.find(tenant => tenant.tenantData.hostingDomain === tenantHostingDomain);
    return found || null;
  }
}
